import React, { useState, useEffect, useCallback } from "react";
import { Info, XCircle, Loader2, AlertCircle } from "lucide-react";
import { getShiftOrders } from "../../../api/shiftService";

const STATUS_CONFIG = {
  DONE: {
    label: "Selesai",
    cls: "bg-green-50 text-green-700 border-green-200",
  },
  RETURNED: {
    label: "Diretur Penuh",
    cls: "bg-purple-50 text-purple-700 border-purple-200",
  },
  PARTIAL_RETURN: {
    label: "Sebagian Diretur",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  PENDING: {
    label: "Pending",
    cls: "bg-gray-100 text-gray-600 border-gray-300",
  },
  CANCEL_REQUESTED: {
    label: "Pengajuan Batal",
    cls: "bg-teal-50 text-teal-700 border-teal-300",
  },
  CANCELLED: {
    label: "Dibatalkan",
    cls: "bg-red-50 text-red-700 border-red-200",
  },
};

const PAYMENT_LABEL = {
  CASH: "Tunai",
  QRIS: "QRIS",
  TRANSFER: "Transfer",
  MIXED: "Campuran",
};
const SELLER_LABEL = {
  INTERNAL: "Internal",
  EXTERNAL: "Umum",
  ONLINE: "Online",
};

function formatDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getPaymentLabel(payments = []) {
  if (!payments.length) return "-";
  const methods = [...new Set(payments.map((p) => p.payment_method))];
  if (methods.length > 1) return "Campuran";
  return PAYMENT_LABEL[methods[0]] || methods[0];
}

export default function ShiftTransactionTable({
  shiftId,
  activeTab,
  onViewDetail,
  onCancel,
  onLoad,
  refreshKey,
}) {
  const [transactions, setTransactions] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("ALL"); // 'ALL' | 'POS' | 'ECATALOG'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  // tab 'cancelled' shows CANCEL_REQUESTED and CANCELLED
  const statusFilter =
    activeTab === "cancelled"
      ? ["CANCEL_REQUESTED", "CANCELLED"]
      : ["DONE", "PENDING", "RETURNED", "PARTIAL_RETURN"];

  const fetchOrders = useCallback(
    async (page = 1) => {
      if (!shiftId) return;
      try {
        setLoading(true);
        setError(null);
        const res = await getShiftOrders(shiftId, {
          page,
          limit: pagination.limit,
          status: statusFilter,
        });
        const total = res?.meta?.total ?? 0;
        setTransactions(res?.data || []);
        setPagination((prev) => ({ ...prev, page, total }));
        onLoad?.(activeTab, total);
      } catch (err) {
        setError(err.message || "Gagal memuat transaksi");
      } finally {
        setLoading(false);
      }
    },
    [shiftId, statusFilter, pagination.limit],
  );

  useEffect(() => {
    fetchOrders(1);
  }, [shiftId, activeTab, refreshKey]);

  const isEcatalogTx = (trx) =>
    trx.source === "ECATALOG_PUBLIC" ||
    trx.source === "ECATALOG" ||
    trx.source === "ONLINE" ||
    trx.seller_type === "ONLINE" ||
    Boolean(trx.public_order_code);

  const filteredTransactions = transactions.filter((trx) => {
    if (sourceFilter === "POS") return !isEcatalogTx(trx);
    if (sourceFilter === "ECATALOG") return isEcatalogTx(trx);
    return true;
  });

  const counts = {
    all: transactions.length,
    pos: transactions.filter((t) => !isEcatalogTx(t)).length,
    ecatalog: transactions.filter((t) => isEcatalogTx(t)).length,
  };

  const totalPages = Math.max(
    1,
    Math.ceil(pagination.total / pagination.limit),
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden space-y-0">
      {/* Source Sub-Tabs */}
      <div className="flex items-center gap-2 p-3 bg-slate-50 border-b border-gray-200">
        {[
          { id: "ALL", label: "Semua Transaksi", count: counts.all },
          { id: "POS", label: "Kasir POS", count: counts.pos },
          { id: "ECATALOG", label: "E-Katalog / Online", count: counts.ecatalog },
        ].map((st) => (
          <button
            key={st.id}
            onClick={() => setSourceFilter(st.id)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              sourceFilter === st.id
                ? "bg-teal-700 text-white"
                : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
            }`}>
            <span>{st.label}</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                sourceFilter === st.id ? "bg-teal-900 text-white" : "bg-slate-100 text-slate-700"
              }`}>
              {st.count}
            </span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="py-4 px-6 font-medium text-sm text-gray-500 w-28 text-center">
                Aksi
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Status
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                No. Transaksi
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Waktu Transaksi
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Nama Pelanggan
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Sumber / Jenis
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Pembayaran
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Total Belanja
              </th>
              <th className="py-4 px-6 font-medium text-sm text-gray-500">
                Kasir
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Loading */}
            {loading && (
              <tr>
                <td colSpan={9} className="py-16 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Memuat transaksi...</p>
                </td>
              </tr>
            )}

            {/* Error */}
            {!loading && error && (
              <tr>
                <td colSpan={9} className="py-16 text-center">
                  <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-600 mb-3">{error}</p>
                  <button
                    onClick={() => fetchOrders(pagination.page)}
                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                    Coba Lagi
                  </button>
                </td>
              </tr>
            )}

            {/* Empty */}
            {!loading && !error && filteredTransactions.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="py-16 text-center text-sm text-gray-400">
                  Belum ada transaksi{" "}
                  {activeTab === "cancelled" ? "dibatalkan" : "selesai"} pada
                  filter ini.
                </td>
              </tr>
            )}

            {/* Rows */}
            {!loading &&
              !error &&
              filteredTransactions.map((trx) => {
                const statusInfo = STATUS_CONFIG[trx.status] ?? {
                  label: trx.status,
                  cls: "bg-gray-100 text-gray-700 border-gray-200",
                };
                const paymentLabel = getPaymentLabel(trx.payments);
                const isEcatalog = isEcatalogTx(trx);
                const cashierName =
                  trx.payments?.[0]?.created_by_user?.name ||
                  trx.payments?.[0]?.created_by_user?.username ||
                  trx.updated_by_user?.name ||
                  trx.updated_by_user?.username ||
                  trx.created_by_user?.name ||
                  trx.created_by_user?.username ||
                  (typeof trx.created_by === "string" && trx.created_by.length < 30 ? trx.created_by : "") ||
                  (isEcatalog ? "E-Katalog" : "-");

                return (
                  <tr
                    key={trx.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex justify-center gap-1.5">
                        {activeTab === "cancelled" ? (
                          <button
                            onClick={() => onViewDetail(trx)}
                            className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                            title="Detail">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => onCancel(trx)}
                              className="w-8 h-8 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center transition-colors"
                              title="Ajukan Pembatalan">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onViewDetail(trx)}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                              title="Detail">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${statusInfo.cls}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900 font-mono">
                      {trx.transaction_number}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateTime(trx.order_date)}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                      {trx.customer_name || "Walk-in"}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500">
                      {isEcatalog ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                          E-Katalog
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-200">
                          Kasir POS
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500">
                      {paymentLabel}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900 font-medium">
                      {trx.shift_paid_amount !== undefined &&
                      trx.shift_paid_amount !== null &&
                      trx.shift_paid_amount > 0 &&
                      trx.shift_paid_amount < (trx.grand_total ?? 0) ? (
                        <div>
                          <span>
                            Rp {(trx.grand_total ?? 0).toLocaleString("id-ID")}
                          </span>
                          <span className="block text-[11px] text-amber-600 font-normal">
                            {trx.payment_status === "PARTIAL" ||
                            trx.status === "PENDING"
                              ? "DP"
                              : "Pelunasan"}{" "}
                            (Dibayar: Rp{" "}
                            {trx.shift_paid_amount.toLocaleString("id-ID")})
                          </span>
                        </div>
                      ) : (
                        <span>
                          Rp{" "}
                          {(trx.grand_total ?? trx.total_amount ?? 0).toLocaleString("id-ID")}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                      {cashierName}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-gray-200 flex items-center justify-between">
        <span className="text-sm text-gray-500">
          {pagination.total > 0
            ? `Menampilkan ${Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}–${Math.min(pagination.page * pagination.limit, pagination.total)} dari ${pagination.total} transaksi`
            : "Tidak ada data"}
        </span>
        <div className="flex gap-1.5">
          <button
            onClick={() => fetchOrders(pagination.page - 1)}
            disabled={pagination.page <= 1 || loading}
            className="px-3 py-1 border rounded text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
            &laquo;
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const start = Math.max(
              1,
              Math.min(pagination.page - 2, totalPages - 4),
            );
            const p = start + i;
            return (
              <button
                key={p}
                onClick={() => fetchOrders(p)}
                disabled={loading}
                className={`px-3 py-1 rounded border text-sm ${
                  p === pagination.page
                    ? "bg-teal-600 text-white border-teal-600"
                    : "text-gray-500 hover:bg-gray-50"
                }`}>
                {p}
              </button>
            );
          })}
          <button
            onClick={() => fetchOrders(pagination.page + 1)}
            disabled={pagination.page >= totalPages || loading}
            className="px-3 py-1 border rounded text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
            &raquo;
          </button>
        </div>
      </div>
    </div>
  );
}
